import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { PaymentType } from "./models/PaymentType";
import {
  archivePaymentType,
  createPaymentType,
  fetchPaymentTypes,
  updatePaymentType,
} from "./slice-extensions/payment-types-slice-extension";

interface PaymentTypesState {
  items: PaymentType[];
  status: LoadingStatus;
  saveStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: PaymentTypesState = {
  items: [],
  status: LoadingStatus.Idle,
  saveStatus: LoadingStatus.Idle,
  lastError: undefined,
};

const paymentTypesSlice = createSlice({
  name: "paymentTypes",
  initialState,
  reducers: {
    clearPaymentTypesError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentTypes.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchPaymentTypes.fulfilled, (state, action: PayloadAction<PaymentType[]>) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload;
      })
      .addCase(fetchPaymentTypes.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(createPaymentType.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(createPaymentType.fulfilled, (state, action: PayloadAction<PaymentType>) => {
        state.saveStatus = LoadingStatus.Idle;
        state.items = [...state.items, action.payload];
      })
      .addCase(createPaymentType.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(updatePaymentType.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(updatePaymentType.fulfilled, (state, action: PayloadAction<PaymentType>) => {
        state.saveStatus = LoadingStatus.Idle;
        state.items = state.items.map((i) => (i.id === action.payload.id ? action.payload : i));
      })
      .addCase(updatePaymentType.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })

      .addCase(archivePaymentType.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.map((i) =>
          i.id === action.payload ? { ...i, isActive: false } : i
        );
      })
      .addCase(archivePaymentType.rejected, (state, action) => {
        state.lastError = action.payload;
      });
  },
});

export const { clearPaymentTypesError } = paymentTypesSlice.actions;

export const selectPaymentTypes = (state: RootState) => state.paymentTypes.items;
export const selectActivePaymentTypes = (state: RootState) =>
  state.paymentTypes.items.filter((p) => p.isActive);
export const selectPaymentTypesStatus = (state: RootState) => state.paymentTypes.status;
export const selectPaymentTypesError = (state: RootState) => state.paymentTypes.lastError;

export default paymentTypesSlice.reducer;
